import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useHomeState } from '@/hooks/use-home-state'
import { useIntroStage } from '@/hooks/use-intro-stage'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { frameBus } from '@/lib/cloud/store'
import { getIntro, introDelay } from '@/lib/intro'
import { ScrambleText, scramble } from '@/components/generals/scramble-text'

import { HOME_SCENES } from '@/constants/scenes'

const SCRAMBLE_MS = 420

const pad = (value: number, length: number): string => String(Math.max(0, Math.floor(value))).padStart(length, '0')

export const SceneHud: FC = (): ReactNode => {
    const state = useHomeState()
    const reduced = useReducedMotion()
    const scene = HOME_SCENES[state.labelScene]
    const yawRef = useRef<HTMLSpanElement>(null)
    const scanRef = useRef<HTMLSpanElement>(null)
    const scrambleUntil = useRef(0)
    const written = useRef({ yaw: '', scan: '' })
    const introStage = useIntroStage()
    const [introEpoch, setIntroEpoch] = useState(0)

    useEffect(() => {
        if (state.labelEpoch === 0 || reduced) return

        scrambleUntil.current = performance.now() + SCRAMBLE_MS
    }, [state.labelEpoch, reduced])

    // In the full intro the figure label and the readouts resolve out of scrambled characters.
    useEffect(() => {
        if (introStage !== 'ui' || reduced || getIntro().mode !== 'full') return

        scrambleUntil.current = performance.now() + SCRAMBLE_MS
        setIntroEpoch(epoch => epoch + 1)
    }, [introStage, reduced])

    useEffect(() => {
        return frameBus.subscribe(info => {
            const yawValue = Math.round(info.yaw) % 360
            const yawText = `Yaw ${pad(yawValue, 3)} / Pitch ${pad(Math.round(info.pitch), 2)}`
            const scanText = `Scan ${pad(info.scene + 1, 2)} / ${pad(info.progress * 100, 3)}%`
            const remaining = scrambleUntil.current - performance.now()
            const progress = 1 - remaining / SCRAMBLE_MS
            const yaw = remaining > 0 ? scramble(yawText, progress) : yawText
            const scan = remaining > 0 ? scramble(scanText, progress) : scanText

            if (yawRef.current && written.current.yaw !== yaw) {
                yawRef.current.textContent = yaw
                written.current.yaw = yaw
            }

            if (scanRef.current && written.current.scan !== scan) {
                scanRef.current.textContent = scan
                written.current.scan = scan
            }
        })
    }, [])

    return (
        <div className='pointer-events-none absolute inset-0 z-[2]'>
            <div className='intro-fade absolute left-gutter right-gutter top-4 desk:right-auto desk:top-[84px]' style={introDelay(100)}>
                <p className='hud text-ink'>
                    <ScrambleText text={scene.figure} epoch={state.labelEpoch + introEpoch} />
                </p>

                <p className='hud text-mute'>
                    <ScrambleText text={scene.grid} epoch={state.labelEpoch + introEpoch} />
                </p>
            </div>

            <div className='intro-fade intro-readout absolute bottom-4 left-gutter desk:bottom-auto desk:left-auto desk:right-gutter desk:top-[84px] desk:text-right' style={introDelay(100)} aria-hidden='true'>
                <p className='hud whitespace-nowrap text-ink'>
                    <span ref={yawRef}>
                        Yaw 036 / Pitch 32
                    </span>
                </p>

                <p className='hud whitespace-nowrap text-mute'>
                    <span ref={scanRef}>
                        Scan 01 / 000%
                    </span>
                </p>
            </div>

            <p className='intro-fade hud absolute bottom-4 right-gutter whitespace-nowrap text-right text-mute desk:bottom-[92px]' style={introDelay(600)} aria-hidden='true'>
                Drag to orbit

                <span className='hidden desk:inline'>
                    &nbsp;/ Arrows to navigate
                </span>
            </p>
        </div>
    )
}
